import { BehaviorSubject, Observable, Subject, lastValueFrom } from 'rxjs';
import { EventEmitter } from '@angular/core';
import { Config } from '@config';
import { CookieService } from '@tools';
import { takeWhile } from 'rxjs/operators';
import { Invokable, CommandResult } from './hub-proxies';

declare let $: any;

let hubCounter = 0;

export class Hub<T> implements Invokable {
    private hubProxy: any;

    private isConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isConnected$: Observable<boolean> = this.isConnected.asObservable();

    private model: BehaviorSubject<T> = new BehaviorSubject(null);
    public model$: Observable<T> = this.model.asObservable();

    private commandResult: BehaviorSubject<CommandResult> = new BehaviorSubject(null);
    public commandResult$: Observable<CommandResult> = this.commandResult.asObservable();

    constructor(
        hubName: string,
        config: Config,
        cookieService: CookieService,
        updatePredicate?: (prevModel, newModel) => boolean
    ) {
        hubCounter++;
        let qs;

        this.connection = $.hubConnection(config.backendUrl, { qs, logging: true });
        this.hubProxy = this.connection.createHubProxy(hubName);

        const win = window as any;
        win.testSocket =   this.hubProxy;

        this.connection.stateChanged((state) => {
            if(state.oldState === 2 && state.newState === 1){
                //reconnected
                this.reSubscribe();
            }

            if(state.oldState === 1 && state.newState === 2){
                console.error('[signalR] disconnected from server!', state)
                //disconnected cause server is down
            }
        });

        this.hubProxy.on('update', (type, data) => {
            console.info('[Hub-update] of type =>', type, 'received: ', data, hubCounter);
            if(data !== null) {
                if (!updatePredicate || updatePredicate(this.model.getValue(), data)) {
                    this.model.next(data);
                }
            }
        });

        this.hubProxy.on('updateCommandStatus', (status: CommandResult) => {
            this.commandResult.next(status);
            console.info('updateCommandStatus: ', status);
        });

        this.hubProxy.on('disconnected', () => {
            console.info('[signalR] disconnected!!!');
        });

        this.connection
            .start({ transport: 'auto' })
            .done((connection) => this.connected(connection))
            .fail((err) => {
                console.info('[signalR] ', err);
                this.connectFailed(err)
            });
    }

    private connection : any;

    public reconnect(){
        return new Promise((resolve, reject) => {
            this.connection
                .start({ transport: 'auto' })
                .done((connection) => {
                    this.connected(connection)
                    resolve(connection);
                })
                .fail((err) => {
                    console.info('[signalR] ', err);
                    this.connectFailed(err);
                    reject(err);
                });
        });
    }

    public waitForConnection() {
        return lastValueFrom(this.isConnected$.pipe(takeWhile((val) => !val)));
    }

    private reSubscribe(){
        for(const topic in this.subscriptions){
            if(this.subscriptions.hasOwnProperty(topic)){
                this.subscribe(topic);
            }
        }
    }

    public async subscribe(topic: string) {
        return new Promise(async(resolve, reject) => {
            await this.waitForConnection()
            await this.invoke('subscribe', topic);
               // this.invoke('subscribe', topic).done(() => {
            this.subscriptions[topic] = true;
            this.onConnect.emit(true);
            console.info('[signalR] subscribed!!!');
            resolve(true);
               // });
        });
    }

    public async unsubscribe(topic: string) {
        this.waitForConnection().then(() => {
            delete this.subscriptions[topic];
            this.hubProxy.invoke('unsubscribe', topic);
        });
    }

    public onConnect: EventEmitter<boolean> = new EventEmitter();

    private subscriptions: any = {}

    public async invoke(command: string, ...args: unknown[]) {
        console.info('invoke', command)
        try {
            return this.hubProxy.invoke(command, ...args);
        }catch(e){
            console.info('[signalR] invoke error! [Hub.invoke]', e);
            await this.reconnect();
            return this.hubProxy.invoke(command, ...args);
        }
    }

    private connected(thing) {
        this.isConnected.next(true);
    }

    private connectFailed(err) {
        console.info('[signalR] Could not connect', err);
    }
}
